export interface SongPart {
  title: string;
  startTime: number; // in seconds
  stopTime: number; // in seconds
  bpmOverride?: number;
}

export interface TrackLink {
  title: string;
  url: string;
}

export interface SongMetadata {
  bpm: number;
  defaultPitchShift: number;
  parts: SongPart[];
  links?: TrackLink[];
}

export interface Track {
  id: string;
  name: string;
  audioFile?: string;
  youtubeId?: string;
  metadata: SongMetadata;
}

export type AppMode = 'backingTrack' | 'metronome' | 'tracks' | 'stats';

export interface PartSettings {
  autoSpeedUp: boolean;
  startBpm: number;
  targetBpm: number;
  increaseBpm: number;
  pitchShift: number;
  duration: number; // in seconds
  /** BPM used when autoSpeedUp is false (fixed-tempo mode). If undefined, the track's base BPM is used. */
  fixedBpm?: number;
}

export interface BackingTrackSettings {
  selectedTrackId: string | null;
  selectedPartIndex: number | null;
  // Settings stored per track and part: { [trackId]: { [partIndex]: PartSettings } }
  partSettings: Record<string, Record<number, PartSettings>>;
  // Remember selected part per track: { [trackId]: partIndex }
  selectedPartsByTrack: Record<string, number>;
}

export interface MetronomeSettings {
  autoSpeedUp: boolean;
  startBpm: number;
  targetBpm: number;
  increaseBpm: number;
  beatsBeforeIncrease: number;
  timeSignature: TimeSignature;
  fixedBpm?: number;
}

export interface TimeSignature {
  /** Top number — beats per bar (e.g. 3 for 3/4, 6 for 6/8). */
  beatsPerBar: number;
  /** Bottom number — the note value that gets one beat (1, 2, 4, 8, 16). */
  beatUnit: number;
  /** 1-indexed beats inside the bar that get the accent click. Defaults to [1]. */
  accents?: number[];
}

export interface TimeSignaturePreset {
  label: string;
  value: TimeSignature;
}

export const TIME_SIGNATURE_PRESETS: TimeSignaturePreset[] = [
  { label: '4/4', value: { beatsPerBar: 4, beatUnit: 4, accents: [1] } },
  { label: '3/4 (waltz)', value: { beatsPerBar: 3, beatUnit: 4, accents: [1] } },
  { label: '2/4', value: { beatsPerBar: 2, beatUnit: 4, accents: [1] } },
  { label: '5/4', value: { beatsPerBar: 5, beatUnit: 4, accents: [1, 4] } },
  { label: '7/4', value: { beatsPerBar: 7, beatUnit: 4, accents: [1, 5] } },
  { label: '6/8', value: { beatsPerBar: 6, beatUnit: 8, accents: [1, 4] } },
  { label: '9/8', value: { beatsPerBar: 9, beatUnit: 8, accents: [1, 4, 7] } },
  { label: '12/8', value: { beatsPerBar: 12, beatUnit: 8, accents: [1, 4, 7, 10] } },
  { label: '7/8 (2+2+3)', value: { beatsPerBar: 7, beatUnit: 8, accents: [1, 3, 5] } },
  { label: '5/8 (2+3)', value: { beatsPerBar: 5, beatUnit: 8, accents: [1, 3] } },
];

export const DEFAULT_TIME_SIGNATURE: TimeSignature = TIME_SIGNATURE_PRESETS[0].value;

export interface PracticeStatsDto {
  todaySeconds: number;
  weekSeconds: number;
  monthSeconds: number;
  currentStreakDays: number;
}

export interface LeaderboardEntryDto {
  rank: number;
  username: string;
  totalSeconds: number;
}

export interface StreakLeaderboardEntryDto {
  rank: number;
  username: string;
  currentStreakDays: number;
}

export interface AppState {
  mode: AppMode;
  backingTrack: BackingTrackSettings;
  metronome: MetronomeSettings;
  isPlaying: boolean;
  currentBpm: number;
  timerEnabled: boolean;
  timerDuration: number; // in seconds
  timerRemaining: number; // in seconds
}
